import { toast } from "@/components/common/Toast"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import {
  useTranslation,
  type TranslationKeys,
} from "@/hooks/useTranslation/useTranslation"
import { cn } from "@/lib/utils"
import { IconLoader2 } from "@tabler/icons-react"
import { useCallback, useEffect, useMemo, useState } from "react"
import { getApiErrorMessage } from "../../api/apiError"
import {
  importsApi,
  useErpSync,
  type ErpAuthMethod,
  type ErpOperationRequest,
  type ErpPreview,
  type ErpSystemId,
} from "../../api/importsApi"
import { McTable, McTd, McTr } from "../shared/McTable"

interface Props {
  open: boolean
  onOpenChange: (open: boolean) => void
  erpType: ErpSystemId
  authMethod: ErpAuthMethod
  /** Objects this ERP exposes (e.g. ["items", "bom"]); >1 shows a selector. */
  objects: string[]
  /** Acquires a fresh Microsoft token — used only for Microsoft-OAuth ERPs. */
  getToken: () => Promise<string>
  onImported: (fileId: string) => void
}

/** i18n key for an object's label, falling back to the raw object name. */
const OBJECT_LABEL_KEY: Record<string, TranslationKeys> = {
  items: "MC_MD_ERP_OBJECT_ITEMS",
  bom: "MC_MD_ERP_OBJECT_BOM",
}

function Cell({ value }: { value: unknown }) {
  if (value === null || value === undefined || value === "")
    return <span className="text-grey6">—</span>
  if (typeof value === "boolean")
    return value ? (
      <span className="text-warning">●</span>
    ) : (
      <span className="text-grey6">—</span>
    )
  return <>{String(value)}</>
}

/**
 * Preview-and-import wizard. On open it fetches a cheap labelled sample (top-20 +
 * true total) so the user sees exactly what they'll pull before committing to the
 * full sync — then hands off to the existing processing → review → commit flow.
 */
export function ErpImportWizard({
  open,
  onOpenChange,
  erpType,
  authMethod,
  objects,
  getToken,
  onImported,
}: Props) {
  const { translate } = useTranslation()
  const { mutate: syncErp, isPending: importing } = useErpSync()
  const [preview, setPreview] = useState<ErpPreview | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [object, setObject] = useState<string>(objects[0] ?? "items")

  // Build the operation body: Microsoft ERPs carry a fresh token; credentials
  // ERPs authenticate server-side from stored credentials.
  const buildBody = useCallback(async (): Promise<ErpOperationRequest> => {
    if (authMethod === "microsoft_oauth") {
      return { microsoftAccessToken: await getToken(), object }
    }
    return { object }
  }, [authMethod, getToken, object])

  useEffect(() => {
    if (!open) {
      setPreview(null)
      setError(null)
      return
    }
    let cancelled = false
    void (async () => {
      setLoading(true)
      setError(null)
      setPreview(null)
      try {
        const data = await importsApi.previewErp(erpType, await buildBody())
        if (!cancelled) setPreview(data)
      } catch (e) {
        if (!cancelled)
          setError(
            getApiErrorMessage(e) ?? translate("MC_MD_ERP_PREVIEW_ERROR"),
          )
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [open, erpType, buildBody, translate])

  async function handleImport() {
    try {
      syncErp(
        { erpType, body: await buildBody() },
        {
          onSuccess: (file) => {
            onOpenChange(false)
            onImported(file.id)
          },
          onError: (e) =>
            toast.error(
              getApiErrorMessage(e) ?? translate("MC_MD_ERP_SYNC_ERROR"),
            ),
        },
      )
    } catch (e) {
      toast.error(getApiErrorMessage(e) ?? translate("MC_MD_ERP_SYNC_ERROR"))
    }
  }

  const objectLabel = (o: string) =>
    OBJECT_LABEL_KEY[o] ? translate(OBJECT_LABEL_KEY[o]) : o

  const cols = useMemo(() => preview?.columns ?? [], [preview])
  const rows = useMemo(() => preview?.sample ?? [], [preview])

  // A column (by index) is numeric only if every present value is a number —
  // those render right-aligned + tabular so prices/quantities line up.
  const numericCols = useMemo(() => {
    const s = new Set<number>()
    cols.forEach((_, ci) => {
      const vals = rows.map((r) => r[ci])
      if (
        vals.some((v) => typeof v === "number") &&
        vals.every((v) => v == null || typeof v === "number")
      )
        s.add(ci)
    })
    return s
  }, [cols, rows])

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl gap-4">
        <DialogHeader>
          <DialogTitle>{translate("MC_MD_ERP_WIZARD_TITLE")}</DialogTitle>
        </DialogHeader>

        {objects.length > 1 && (
          <div className="flex items-center gap-2">
            <span className="text-[12.5px] text-grey5">
              {translate("MC_MD_ERP_OBJECT_LABEL")}
            </span>
            <div className="inline-flex rounded-lg border border-grey4 p-0.5">
              {objects.map((o) => (
                <button
                  key={o}
                  type="button"
                  onClick={() => setObject(o)}
                  disabled={importing}
                  className={cn(
                    "rounded-md px-3 py-1 text-[12.5px] font-medium transition-colors",
                    o === object
                      ? "bg-grey4 text-white"
                      : "text-grey5 hover:text-white",
                  )}
                >
                  {objectLabel(o)}
                </button>
              ))}
            </div>
          </div>
        )}

        {loading && (
          <div className="flex items-center gap-2 py-16 text-sm text-grey5">
            <IconLoader2 className="animate-spin" size={16} />
            {translate("MC_MD_ERP_WIZARD_LOADING")}
          </div>
        )}

        {error && !loading && (
          <div className="py-10 text-center text-sm text-red1">{error}</div>
        )}

        {preview && !loading && (
          <div className="min-w-0">
            <p className="mb-3 text-[12.5px] text-grey5">
              {translate("MC_MD_ERP_WIZARD_SAMPLE_LABEL", {
                shown: rows.length,
                total: preview.totalCount.toLocaleString(),
              })}
            </p>
            <McTable stickyHeader headers={cols} className="max-h-[52vh]">
              <tbody>
                {rows.map((r, i) => (
                  <McTr key={i}>
                    {cols.map((c, ci) => (
                      <McTd
                        key={`${c}-${ci}`}
                        variant={
                          ci === 0
                            ? "accent"
                            : numericCols.has(ci)
                              ? "default"
                              : "primary"
                        }
                        mono={ci === 0 || numericCols.has(ci)}
                        className={cn(
                          "whitespace-nowrap",
                          numericCols.has(ci) && "text-right tabular-nums",
                        )}
                      >
                        <Cell value={(r as unknown[])[ci]} />
                      </McTd>
                    ))}
                  </McTr>
                ))}
              </tbody>
            </McTable>
          </div>
        )}

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            {translate("MC_MD_ERP_WIZARD_CANCEL")}
          </Button>
          <Button
            onClick={handleImport}
            disabled={!preview || importing || loading}
          >
            {importing && <IconLoader2 className="animate-spin" size={14} />}
            {preview
              ? translate("MC_MD_ERP_WIZARD_IMPORT", {
                  count: preview.totalCount.toLocaleString(),
                })
              : translate("MC_MD_ERP_WIZARD_IMPORT_GENERIC")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
